import { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import { useI18n } from '../hooks/useI18n';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
    XIcon,
    Bell as BellIcon,
    Package as PackageIcon,
    CurrencyDollar as CurrencyDollarIcon,
    Users as UsersIcon,
    Gear as GearIcon
} from '@phosphor-icons/react';

interface Notification {
    id: string;
    title: string;
    message: string;
    category: string;
    severity: string;
    action_url?: string;
    action_label?: string;
    created_at: string;
    is_acknowledged: boolean;
}

interface NotificationCounts {
    total: number;
    unacknowledged: number;
    by_category: Record<string, number>;
    by_severity: Record<string, number>;
}

const severityColors = {
    info: 'bg-blue-500',
    warning: 'bg-yellow-500',
    critical: 'bg-red-500',
};

const categoryIcons = {
    inventory: PackageIcon,
    finance: CurrencyDollarIcon,
    people: UsersIcon,
    system: GearIcon,
};

export default function NotificationInbox() {
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [counts, setCounts] = useState<NotificationCounts | null>(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { t } = useI18n();

    const fetchCounts = async () => {
        try {
            const response = await apiFetch('/api/notifications/count');
            if (response.ok) {
                const data = await response.json();
                setCounts(data);
            }
        } catch (error) {
            console.error('Failed to fetch notification counts:', error);
        }
    };

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await apiFetch('/api/notifications/?limit=10');
            if (response.ok) {
                const data = await response.json();
                setNotifications(data.notifications || []);
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const acknowledgeNotification = async (notificationId: string) => {
        try {
            const response = await apiFetch(`/api/notifications/${notificationId}/acknowledge`, {
                method: 'POST',
            });

            if (response.ok) {
                // Remove from list or mark as acknowledged
                setNotifications(prev => prev.filter(n => n.id !== notificationId));
                // Immediately refresh counts to update badge
                await fetchCounts();
                toast.success('Notification acknowledged');
            }
        } catch (error) {
            console.error('Failed to acknowledge notification:', error);
            toast.error('Failed to acknowledge notification');
        }
    };

    const handleNotificationClick = (notification: Notification) => {
        if (notification.action_url) {
            navigate({ to: notification.action_url });
            setOpen(false);
        }
    };

    const handleNotificationItemClick = () => {
        navigate({ to: '/notifications' });
        setOpen(false);
    };

    // Fetch counts on mount and set up polling
    useEffect(() => {
        fetchCounts();
        const interval = setInterval(fetchCounts, 300000); // Poll every 5 minutes
        return () => clearInterval(interval);
    }, []);

    // Fetch notifications when popover opens
    useEffect(() => {
        if (open) {
            fetchNotifications();
        }
    }, [open]);

    const formatTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (seconds < 60) return t('time.justNow', 'common');
        if (seconds < 3600) return `${Math.floor(seconds / 60)}${t('time.minutesAgo', 'common')}`;
        if (seconds < 86400) return `${Math.floor(seconds / 3600)}${t('time.hoursAgo', 'common')}`;
        return `${Math.floor(seconds / 86400)}${t('time.daysAgo', 'common')}`;
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative">
                    <BellIcon size={20} weight="duotone" />
                    {counts && counts.unacknowledged > 0 && (
                        <Badge
                            variant="destructive"
                            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                        >
                            {counts.unacknowledged > 9 ? '9+' : counts.unacknowledged}
                        </Badge>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-96 p-0" align="end">
                <div className="flex items-center justify-between p-4 border-b">
                    <div>
                        <h3 className="font-semibold text-lg">{t('notifications.title', 'common')}</h3>
                        {counts && counts.unacknowledged > 0 && (
                            <p className="text-sm text-muted-foreground">
                                {counts.unacknowledged} {t('notifications.unread', 'common')}
                            </p>
                        )}
                    </div>
                </div>

                <ScrollArea className="h-[400px]">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <BellIcon size={48} weight="duotone" className="text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground">{t('notifications.noNew', 'common')}</p>
                        </div>
                    ) : (
                        <div className="">
                            {notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className="p-2"
                                >
                                    <div
                                        className="group flex items-start gap-2 hover:bg-muted transition-colors p-3 rounded-md cursor-pointer"
                                        onClick={handleNotificationItemClick}
                                    >
                                        {/* Category Icon & Severity Indicator */}
                                        <div className="relative flex-shrink-0">
                                            <div className="text-muted-foreground">
                                                {(() => {
                                                    const IconComponent = categoryIcons[notification.category as keyof typeof categoryIcons];
                                                    return IconComponent ? <IconComponent size={32} weight="duotone" /> : <BellIcon size={32} weight="duotone" />;
                                                })()}
                                            </div>
                                            <div
                                                className={`absolute -top-1 -right-1 h-3 w-3 rounded-full ${severityColors[notification.severity as keyof typeof severityColors]
                                                    }`}
                                            />
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <h4 className="font-medium text-sm leading-tight">
                                                    {notification.title}
                                                </h4>
                                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                                    {formatTimeAgo(notification.created_at)}
                                                </span>
                                            </div>
                                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                                {notification.message}
                                            </p>

                                            {/* Actions */}
                                            <div className="max-h-0 overflow-hidden group-hover:max-h-20 transition-all duration-200">
                                                <div className="flex items-center justify-end gap-2 mt-3">
                                                    {notification.action_url && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-7 text-xs"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleNotificationClick(notification);
                                                            }}
                                                        >
                                                            {notification.action_label || t('actions.view', 'common')}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-7 text-xs"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            acknowledgeNotification(notification.id);
                                                        }}
                                                    >
                                                        <XIcon />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </ScrollArea>

                <Separator />
                <div className="p-2">
                    <Button
                        variant="ghost"
                        className="w-full text-sm"
                        onClick={() => {
                            navigate({ to: '/notifications' });
                            setOpen(false);
                        }}
                    >
                        {t('notifications.viewPrevious', 'common')}
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
